import React, { useState } from 'react';
import { Box, Card, CardContent, CardHeader, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import PhxBadge from '_lex/_atoms/PhxBadge';
import DetailList from '_lex/_atoms/DetailList';
import GridSection from 'components/_layouts/GridSection';
import datetimeHelper from 'helpers/datetimeHelper';
import { useEisFlag } from '../../../_data_sources/useEisFlag';
import { useEisFlagIncidents } from '../../../_data_sources/useEisFlagIncidents';
import type { EisFlagIncident } from '../../../_types';
import { getStatusBadgeColor } from '../../../_utils/eisStatusHelpers';

type Props = {
  flagId: string;
};

const FlagSummaryCard: React.FC<Props> = ({ flagId }) => {
  const [activeTab, setActiveTab] = useState(0);

  const flagQuery = useEisFlag(flagId, {}, (err) => {
    console.error('Error fetching EIS flag:', err);
  });

  const incidentsQuery = useEisFlagIncidents(flagId, {}, (err) => {
    console.error('Error fetching EIS flag incidents:', err);
  });

  const flag = flagQuery.data;
  const incidents = incidentsQuery.data?.content ?? [];
  const incidentsCount = incidentsQuery.data?.count ?? 0;

  const formatFlagStatus = () => {
    if (!flag?.status) return '--';
    return (
      <PhxBadge
        color={getStatusBadgeColor(flag.status)}
        size='sm'
      >
        {t(`performanceReporting.eis.details.flaggedPersonnelDetails.status.${flag.status.toLowerCase()}`)}
      </PhxBadge>
    );
  };

  const formatFlaggedScore = () => {
    if (flag?.flaggedScore == null) return '--';
    const score = flag.flaggedScore;
    const over = flag.ruleThreshold != null ? score - flag.ruleThreshold : 0;
    return (
      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
      >
        <Typography variant='bodySm'>{`${score} pts`}</Typography>
        {over > 0 && (
          <PhxBadge
            color='default'
            size='sm'
          >
            {t('performanceReporting.eis.details.flaggedPersonnel.overThreshold', '', { count: String(over) })}
          </PhxBadge>
        )}
      </Stack>
    );
  };

  const formatTimeWindow = () => {
    if (flag?.timeWindow == null) return '--';
    return `${flag.timeWindow} ${t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.overview.daysUnit')}`;
  };

  const overviewItems = [
    {
      label: t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.overview.flagStatus'),
      value: formatFlagStatus(),
    },
    {
      label: t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.overview.triggeredRule'),
      value: flag?.triggeredRule ?? '--',
    },
    {
      label: t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.overview.flaggedDate'),
      value: flag?.flaggedDate ? datetimeHelper.formatDate(flag.flaggedDate, { missingDisplay: '--' }) : '--',
    },
    {
      label: t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.overview.ruleThreshold'),
      value: flag?.ruleThreshold != null ? `${flag.ruleThreshold} pts` : '--',
    },
    {
      label: t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.overview.flaggedScore'),
      value: formatFlaggedScore(),
    },
    {
      label: t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.overview.timeWindow'),
      value: formatTimeWindow(),
    },
  ];

  const incidentColumns = React.useMemo(
    () => ({
      caseNumber: {
        header: t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.incidents.columns.caseNumber'),
        flex: 1.5,
        sortable: false,
        valueGetter: (_: unknown, row: EisFlagIncident) => row?.caseNumber ?? '',
        renderer: (params: { row: EisFlagIncident }) => (
          <Typography variant='body1'>{params.row?.caseNumber ?? '--'}</Typography>
        ),
      },
      template: {
        header: t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.incidents.columns.template'),
        flex: 2,
        sortable: false,
        valueGetter: (_: unknown, row: EisFlagIncident) => row?.template ?? '',
        renderer: (params: { row: EisFlagIncident }) => (
          <Typography variant='body1'>{params.row?.template ?? '--'}</Typography>
        ),
      },
      incidentDate: {
        header: t(
          'performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.incidents.columns.incidentDate',
        ),
        flex: 1.5,
        sortable: false,
        valueGetter: (_: unknown, row: EisFlagIncident) => row?.incidentDate ?? '',
        renderer: (params: { row: EisFlagIncident }) => (
          <Typography variant='body1'>
            {datetimeHelper.formatDate(params.row?.incidentDate, { missingDisplay: '--' })}
          </Typography>
        ),
      },
      riskScore: {
        header: t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.incidents.columns.riskScore'),
        flex: 1,
        sortable: false,
        valueGetter: (_: unknown, row: EisFlagIncident) => row?.riskScore ?? 0,
        renderer: (params: { row: EisFlagIncident }) => (
          <Typography variant='body1'>{params.row?.riskScore ?? '--'}</Typography>
        ),
      },
    }),
    [t],
  );

  if (flagQuery.isLoading) {
    return (
      <Card className='lex-card'>
        <CardHeader title={t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.title')} />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (flagQuery.isError) {
    return (
      <Card className='lex-card'>
        <CardHeader title={t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.title')} />
        <CardContent>
          <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
            {t('performanceReporting.eis.details.flaggedPersonnelDetails.errorLoading')}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='lex-card'>
      <CardHeader title={t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.title')} />
      <CardContent>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab label={t('performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.tabs.overview')} />
          <Tab
            label={t(
              'performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.tabs.contributingIncidents',
            )}
          />
        </Tabs>

        {activeTab === 0 && <DetailList items={overviewItems} />}

        {activeTab === 1 && (
          <Box>
            {incidentsQuery.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : incidentsQuery.isError ? (
              <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
                {t('performanceReporting.eis.details.flaggedPersonnelDetails.errorLoading')}
              </Box>
            ) : incidents.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography
                  variant='body1'
                  color='text.secondary'
                >
                  {t(
                    'performanceReporting.eis.details.flaggedPersonnelDetails.flagSummary.incidents.emptyState.title',
                  )}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ minHeight: 300 }}>
                <GridSection
                  columns={incidentColumns}
                  data={incidents}
                  query={incidentsQuery}
                  totalCount={incidentsCount}
                  gridKey={`flag-incidents-${flagId}`}
                  gridProps={{
                    getRowId: (row: EisFlagIncident) => row.caseNumber,
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FlagSummaryCard;
